import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { cpf } from 'cpf-cnpj-validator';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/AuthService/auth.service';
import { FuncionarioService } from 'src/app/services/FuncionarioService/funcionario.service';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';
import { TipoPerfilService } from 'src/app/services/TipoPerfil/tipoPerfil.service';

@Component({
  selector: 'app-cadastro-funcionario-empresa',
  templateUrl: './cadastro-funcionario.component.html',
  styleUrls: ['./cadastro-funcionario.component.scss']
})
export class CadastroFuncionarioEmpresaComponent implements OnInit, OnDestroy {
  private subscription$ = new Subscription();

  form = this.fb.group({
    NM_FUNC: ['', [Validators.required]],
    TX_CPF_FUNC: ['', [Validators.required]],
    TX_MAIL_FUNC: [
      '',
      [
        Validators.required,
        Validators.email,
        Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')
      ]
    ],
    TX_TELE_FUNC: ['', [Validators.required]],
    TX_USER_NAME: ['', [Validators.required]],
    FK_PERF: [0],
    NU_TRNO_FUNC: [3],
    TX_SENH_FUNC: ['', [Validators.required, Validators.maxLength(15)]],
    TX_SENH_FUNC_CNFRM: ['', [Validators.required, Validators.maxLength(15)]]
  });

  title: string = 'Cadastrar Funcionário';
  isLoading: boolean = false;
  statusInput: boolean = false;
  activeRoute: string = '';
  pkFuncionario: number = 0;
  isEmployeeLogin: boolean = false;

  tipoPerfilOptions: Array<{ name: string; value: number }> = [];
  turnoOptions: Array<{ name: string; value: number }> = [
    { name: '07 às 19h', value: 1 },
    { name: '19h às 07h', value: 2 },
    { name: '24h', value: 3 }
  ];

  isLengthValid = false;
  hasNumber = false;
  hasLowercase = false;
  hasUppercase = false;
  hasSpecialChar = false;
  passwordsMatch = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private location: Location,
    private authService: AuthService,
    private funcionarioService: FuncionarioService,
    private tipoPerfilService: TipoPerfilService,
    private swalAlert: SwalAlertService
  ) {}

  ngOnInit(): void {
    this.isEmployeeLogin = this.authService.getCurrentUserType() === 'FUNC';
    this.getTipoPerfil();
    this.stateForms();
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  getTipoPerfil(): void {
    this.tipoPerfilService.GetTipoPerfil().subscribe({
      next: response => {
        this.tipoPerfilOptions = (response || []).map((item: any) => ({
          name: item.NM_PERF,
          value: item.PK_PERF
        }));

        if (this.isCreateMode() && this.tipoPerfilOptions.length > 0) {
          this.form.controls['FK_PERF'].setValue(this.tipoPerfilOptions[0].value);
        }
      }
    });
  }

  stateForms(): void {
    this.subscription$.add(
      this.route.queryParams.subscribe(params => {
        const ownId = Number(localStorage.getItem('id'));
        const idFromRoute = Number(params['codigo'] || 0);

        if (this.isEmployeeLogin && idFromRoute && idFromRoute !== ownId) {
          this.router.navigate(['empresa/listagem/funcionario']);
          return;
        }

        const idToLoad = this.isEmployeeLogin
          ? ownId
          : idFromRoute > 0
            ? idFromRoute
            : 0;

        if (!idToLoad) {
          return;
        }

        this.title = params['modoFuncionario'] === 'Visualizar' ? 'Visualizar Funcionário' : 'Editar Funcionário';

        this.isLoading = true;
        this.funcionarioService.GetFuncionarioById(idToLoad).subscribe({
          next: response => {
            this.pkFuncionario = response.PK_FUNC;
            this.form.patchValue({
              NM_FUNC: response.NM_FUNC ?? '',
              TX_CPF_FUNC: response.TX_CPF_FUNC ?? '',
              TX_MAIL_FUNC: response.TX_MAIL_FUNC ?? '',
              TX_TELE_FUNC: response.TX_TELE_FUNC ?? '',
              TX_USER_NAME: response.TX_USER_NAME ?? '',
              FK_PERF: response.FK_PERF ?? '',
              NU_TRNO_FUNC: response.NU_TRNO_FUNC ?? ''
            });

            this.form.controls['TX_SENH_FUNC'].clearValidators();
            this.form.controls['TX_SENH_FUNC_CNFRM'].clearValidators();
            this.form.controls['TX_SENH_FUNC'].updateValueAndValidity();
            this.form.controls['TX_SENH_FUNC_CNFRM'].updateValueAndValidity();

            if (params['modoFuncionario'] === 'Visualizar') {
              this.statusInput = true;
              this.form.disable();
            }

            if (this.isEmployeeLogin) {
              this.form.controls['FK_PERF'].disable();
              this.form.controls['NU_TRNO_FUNC'].disable();
            }

            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          }
        });
      })
    );
  }

  isCreateMode(): boolean {
    return !this.title.includes('Editar') && !this.title.includes('Visualizar');
  }

  confirmacaoSenha(): void {
    const senha = this.form.controls['TX_SENH_FUNC'].value || '';
    const novaSenha = this.form.controls['TX_SENH_FUNC_CNFRM'].value || '';

    this.isLengthValid = senha.length >= 8;
    this.hasNumber = /\d/.test(senha);
    this.hasLowercase = /[a-z]/.test(senha);
    this.hasUppercase = /[A-Z]/.test(senha);
    this.hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(senha);
    this.passwordsMatch = senha === novaSenha;
  }

  createPayload() {
    const raw = this.form.getRawValue();
    const senha = raw.TX_SENH_FUNC || null;

    const perfilDefault = this.tipoPerfilOptions.length
      ? this.tipoPerfilOptions[0].value
      : null;

    const perfilSelecionado = Number(raw.FK_PERF || perfilDefault || 0);

    return {
      NM_FUNC: raw.NM_FUNC ?? '',
      TX_CPF_FUNC: String(raw.TX_CPF_FUNC || '').replace(/\D/g, ''),
      TX_MAIL_FUNC: raw.TX_MAIL_FUNC ?? '',
      TX_TELE_FUNC: String(raw.TX_TELE_FUNC || '').replace(/\D/g, ''),
      TX_USER_NAME: raw.TX_USER_NAME ?? '',
      TX_SENH_FUNC: senha,
      FK_PERF: perfilSelecionado,
      NU_TRNO_FUNC: Number(raw.NU_TRNO_FUNC || 3),
      NU_STTS_FUNC: 1,
      NU_ROLE_FUNC: 1
    };
  }

  postarDados(): void {
    const payload = this.createPayload();

    if (this.form.invalid) {
      this.swalAlert.showAlert(
        'Atenção!',
        'Verifique se todos os campos obrigatórios foram preenchidos corretamente.',
        'warning'
      );
      return;
    }

    if (!cpf.isValid(payload.TX_CPF_FUNC)) {
      this.swalAlert.showAlert('Atenção!', 'O CPF informado é inválido.', 'warning');
      return;
    }

    if (!payload.FK_PERF || payload.FK_PERF <= 0) {
      this.swalAlert.showAlert(
        'Atenção!',
        'Não foi possível identificar um perfil válido para o funcionário.',
        'warning'
      );
      return;
    }

    if (this.isCreateMode()) {
      if (!this.passwordsMatch) {
        this.swalAlert.showAlert('Atenção!', 'As senhas devem ser iguais.', 'warning');
        return;
      }

      this.isLoading = true;
      this.funcionarioService.CreateFuncionario(payload).subscribe({
        next: () => {
          this.isLoading = false;
          this.router.navigate(['empresa/listagem/funcionario']);
        },
        error: () => {
          this.isLoading = false;
        }
      });

      return;
    }

    this.isLoading = true;
    this.funcionarioService.EditarFuncionario(this.pkFuncionario, payload).subscribe({
      next: () => {
        this.isLoading = false;
        this.router.navigate(['empresa/listagem/funcionario']);
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  goTo(route: string): void {
    this.router.navigate([route]);
    setTimeout(() => {
      this.activeRoute = this.router.url;
      this.cd.detectChanges();
    }, 200);
  }
}
